import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight, CheckCircle, TrendingUp, BarChart3 } from 'lucide-react';

const Guides = () => {
  const guides = [
    {
      id: 1,
      title: 'Getting Started with Online Casinos in the UK',
      slug: 'getting-started-online-casinos-uk',
      description: 'Your complete beginner\'s guide to navigating the UK online casino landscape with confidence and knowledge.'
    },
    {
      id: 2,
      title: 'Understanding UK Gambling Regulations and Licensing',
      slug: 'uk-gambling-regulations-licensing',
      description: 'Learn about the UK Gambling Commission, player protections, and what makes a casino legally compliant.'
    },
    {
      id: 3,
      title: 'Choosing the Right Online Casino for You',
      slug: 'choosing-right-online-casino',
      description: 'Discover the key factors that separate excellent casinos from the rest, tailored to your gaming preferences.'
    },
    {
      id: 4,
      title: 'Registration and Account Verification Process',
      slug: 'registration-account-verification',
      description: 'Step-by-step guidance on creating your casino account and completing the verification process smoothly.'
    },
    {
      id: 5,
      title: 'Making Deposits and Withdrawals Safely',
      slug: 'deposits-withdrawals-safely',
      description: 'Master payment methods, understand transaction times, and ensure your financial security at online casinos.'
    },
    {
      id: 6,
      title: 'Understanding Bonuses and Wagering Requirements',
      slug: 'bonuses-wagering-requirements',
      description: 'Decode bonus terms, wagering requirements, and learn how to maximize your bonus value effectively.'
    },
    {
      id: 7,
      title: 'Game Selection Strategies and Tips',
      slug: 'game-selection-strategies-tips',
      description: 'Expert advice on choosing games, understanding RTP, volatility, and developing winning strategies.'
    },
    {
      id: 8,
      title: 'Responsible Gambling and Player Protection',
      slug: 'responsible-gambling-player-protection',
      description: 'Essential information on staying safe, setting limits, recognizing problem gambling, and accessing support.'
    },
    {
      id: 9,
      title: 'Customer Support and Problem Resolution',
      slug: 'customer-support-problem-resolution',
      description: 'Navigate customer service channels, understand dispute resolution, and know your rights as a player.'
    }
  ];

  // Chart data
  const playerGrowthData = [
    { year: '2021', players: 65 },
    { year: '2022', players: 72 },
    { year: '2023', players: 78 },
    { year: '2024', players: 85 },
    { year: '2025', players: 92 }
  ];

  const paymentMethodData = [
    { method: 'Credit/Debit Cards', usage: 45 },
    { method: 'E-Wallets', usage: 32 },
    { method: 'Bank Transfer', usage: 15 },
    { method: 'Cryptocurrency', usage: 8 }
  ];

  const gamePreferenceData = [
    { game: 'Slots', popularity: 68 },
    { game: 'Blackjack', popularity: 42 },
    { game: 'Roulette', popularity: 38 },
    { game: 'Poker', popularity: 25 },
    { game: 'Live Casino', popularity: 55 }
  ];

  const maxValue = 100;

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-pink-500 to-green-500 rounded-full mb-6">
              <BookOpen className="w-10 h-10 text-white" />
            </div>
            <h1 className="pb-2 text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-pink-600 to-green-600 bg-clip-text text-transparent">
              How to Play Online Casinos in the UK
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Comprehensive guides crafted by industry experts to help you navigate the UK online casino world with confidence, knowledge, and responsible gaming practices.
            </p>
          </div>

          {/* About Our Guides Section */}
          <div className="glass-strong rounded-3xl p-8 md:p-12 mb-16">
            <div className="flex items-center space-x-3 mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">How We Write Our Guides</h2>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
              <p>
                At Crown Jackpot, we understand that navigating the world of online casinos can be overwhelming, especially for newcomers to the digital gaming landscape. Our comprehensive guides are meticulously crafted by a team of seasoned industry professionals who have dedicated years to understanding every facet of online gambling in the United Kingdom.
              </p>
              <p>
                Our approach to guide creation is founded on three core principles: accuracy, accessibility, and accountability. We don't simply compile information from various sources – we actively test casinos, verify claims, and conduct thorough research to ensure every piece of advice we offer is both current and actionable. Our team regularly visits casino platforms, registers accounts, tests payment methods, and evaluates customer support channels to provide first-hand insights you won't find elsewhere.
              </p>
              <p>
                Each guide undergoes a rigorous review process that examines regulatory compliance, technical accuracy, and practical applicability. We cross-reference our recommendations with UK Gambling Commission guidelines, verify licensing information, and ensure all advice aligns with responsible gambling best practices. Our content is regularly updated to reflect changes in regulations, industry standards, and emerging technologies that shape the online casino experience.
              </p>
              <p>
                What sets our guides apart is our commitment to transparency. We explain not just what to do, but why it matters. We break down complex concepts like wagering requirements, RTP percentages, and regulatory frameworks into digestible, practical advice. We don't shy away from discussing challenges, potential pitfalls, and red flags – our goal is to empower you with complete information, not just the positive aspects.
              </p>
              <p>
                Our writing style balances professionalism with approachability. We understand that whether you're a complete beginner or an experienced player seeking deeper knowledge, clear communication is essential. Every guide is structured to guide you step-by-step through processes, offer actionable tips you can implement immediately, and provide the context needed to make informed decisions.
              </p>
              <p>
                We maintain complete editorial independence in our guide creation process. Our recommendations and advice are never influenced by commercial partnerships or affiliate relationships. While we may earn commissions from some casinos we recommend, this relationship never affects our editorial judgment, review process, or the guidance we provide. Your trust and informed decision-making are our top priorities.
              </p>
              <p>
                Our guides are living documents that evolve with the industry. As new regulations emerge, technologies develop, and player preferences shift, we continuously refine our content to ensure relevance and accuracy. We monitor feedback from our community, track industry developments, and incorporate lessons learned from real-world casino experiences to enhance the value of every guide we publish.
              </p>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Chart 1: Player Growth */}
            <div className="glass-strong rounded-2xl p-6">
              <div className="flex items-center space-x-2 mb-4">
                <TrendingUp className="w-6 h-6 text-green-600" />
                <h3 className="text-xl font-bold text-gray-800">UK Online Casino Players</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">Growth in millions of active players</p>
              <div className="space-y-3">
                {playerGrowthData.map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-700 font-medium">{item.year}</span>
                      <span className="text-gray-600">{item.players}M</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-pink-500 to-green-500 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${(item.players / 100) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Chart 2: Payment Methods */}
            <div className="glass-strong rounded-2xl p-6">
              <div className="flex items-center space-x-2 mb-4">
                <BarChart3 className="w-6 h-6 text-pink-600" />
                <h3 className="text-xl font-bold text-gray-800">Payment Methods</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">Usage percentage by UK players</p>
              <div className="space-y-4">
                {paymentMethodData.map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-700 font-medium">{item.method}</span>
                      <span className="text-gray-600">{item.usage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className="bg-gradient-to-r from-green-500 to-pink-500 h-4 rounded-full transition-all duration-500"
                        style={{ width: `${item.usage}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Chart 3: Game Preferences */}
            <div className="glass-strong rounded-2xl p-6">
              <div className="flex items-center space-x-2 mb-4">
                <BarChart3 className="w-6 h-6 text-green-600" />
                <h3 className="text-xl font-bold text-gray-800">Game Preferences</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">Popularity among UK players (%)</p>
              <div className="space-y-3">
                {gamePreferenceData.map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-700 font-medium">{item.game}</span>
                      <span className="text-gray-600">{item.popularity}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-pink-500 to-green-500 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${item.popularity}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Guides Grid */}
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 bg-gradient-to-r from-pink-600 to-green-600 bg-clip-text text-transparent">
              Complete Guide Collection
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {guides.map((guide) => (
                <Link
                  key={guide.id}
                  to={`/guides/${guide.slug}`}
                  className="glass-strong rounded-2xl p-6 hover:transform hover:scale-105 transition-all duration-300 group"
                  data-discover="true"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-green-500 rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {guide.id}
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-green-600 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600 transition-colors">
                    {guide.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {guide.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Guides;
